/**
 * AttendanceTableView.tsx
 * 출석부 - 테이블/리스트 뷰
 *
 * Features:
 * - 학생별 출석 현황 테이블
 * - 정렬/필터/검색 기능
 * - 출석 상태별 색상 표시
 * - ⭐ 교시 탭 (현재 / 1~N교시): 회의에서 사용자 요구사항. 교시 선택 시
 *   그 교시 시점의 학생별 출결 상태로 표시. (2026-05-26)
 */

import React, { useState, useMemo, useEffect } from 'react';
import { RuntimeSeat } from '../../repositories/SeatOperationRepository';
import { apiClient } from '../../../../services/authApi';

interface AttendanceTableViewProps {
  seats: RuntimeSeat[];
  onStudentClick?: (seatId: string) => void;
  roomId?: string | null;       // ⭐ 교시 탭용 — 현재 선택된 룸
  targetDate?: string;          // YYYY-MM-DD, 미지정 시 오늘
}

type SortField = 'seat_code' | 'student_name' | 'status' | 'check_in_time' | 'problem';
type SortDirection = 'asc' | 'desc';
type StatusFilter = 'all' | 'present' | 'late' | 'out' | 'absent' | 'exempt' | 'not_scheduled';

// 교시 grid 응답 학생 행
interface GridStudent {
  student_id: string;
  name: string;
  seat_number?: number;
  statuses: Record<string | number, string>;  // {period_index: status_code}
}

// 교시 메타 — 백엔드 응답: period_number / period_name / start_time / end_time
interface PeriodMeta {
  period_number?: number;
  period_name?: string;
  period_index?: number;  // 폴백
  index?: number;          // 폴백
  label?: string;          // 폴백
  name?: string;           // 폴백
  start_time?: string;
  end_time?: string;
}

// 그리드 상태 코드 → 화면 상태 코드 매핑
// (build_period_attendance_grid 반환값: present/late/absent/absent_excused/no_schedule)
const GRID_TO_STATUS: Record<string, string> = {
  present: 'present',
  late: 'present_late',
  absent: 'absent',
  // 그리드의 outing 은 '스케줄 있는 교시가 끝날 때까지 안 돌아옴' = 확정 미복귀다.
  // 평범한 'out'(외출 중)으로 두면 정렬이 미출석보다 아래로 내려가 눈에 안 띈다.
  outing: 'out_overdue',
  absent_excused: 'exempt',
  no_schedule: 'not_scheduled',
};

const STATUS_BADGES: Record<string, { label: string; className: string }> = {
  present: { label: '입실중', className: 'bg-green-100 text-green-800' },
  present_late: { label: '지각', className: 'bg-orange-100 text-orange-800' },
  arriving: { label: '입실예정', className: 'bg-orange-100 text-orange-800' },
  out: { label: '외출', className: 'bg-blue-100 text-blue-800' },
  out_warning: { label: '외출', className: 'bg-blue-100 text-blue-800 ring-2 ring-orange-400' },  // 미복귀 경고 (5분 이내)
  out_overdue: { label: '외출', className: 'bg-blue-100 text-blue-800 ring-2 ring-red-500' },     // 미복귀 심각 (5분 초과)
  absent: { label: '미출석', className: 'bg-red-100 text-red-800' },
  exempt: { label: '출석면제', className: 'bg-purple-100 text-purple-800' },
  exited: { label: '퇴실', className: 'bg-gray-100 text-gray-600' },
  not_scheduled: { label: '스케줄없음', className: 'bg-gray-100 text-gray-600' },
};

// 문제 우선 정렬 순위 — 숫자 작을수록 위(먼저). 관리자가 손봐야 할 학생을 맨 위로.
const PROBLEM_RANK: Record<string, number> = {
  out_overdue: 0,     // 외출 미복귀(심각)
  out_warning: 1,     // 외출 미복귀(경고)
  absent: 2,          // 미출석 ← 핵심 문제
  present_late: 3,    // 지각
  arriving: 4,        // 입실예정(아직 안 옴)
  out: 5,             // 외출(정상)
  present: 6,         // 입실중(정상)
  exempt: 7,          // 출석면제
  exited: 8,          // 퇴실
  not_scheduled: 9,   // 스케줄없음
};

const OUTING_LABELS: Record<string, string> = {
  meal: '🍚 식사',
  hospital: '🏥 병원',
  academy: '📚 학원',
  rest: '☕ 휴식',
  toilet: '🚻 화장실',
  other: '📝 기타',
};

// 전화번호 → tel: 링크(숫자/＋만). 폰(또는 연결된 PC)에서 누르면 통화앱이 번호 채운 채 뜸.
const telHref = (p?: string | null) => (p ? p.replace(/[^0-9+]/g, '') : '');

// 순찰 메모 종류 라벨/이모지 (비고에 클릭 없이 표시)
const NOTE_LABELS: Record<string, string> = {
  sleep: '😴 졸음',
  off_task: '🌀 딴짓',
  leave: '🚶 자리비움',
  bathroom: '🚻 화장실',
  phone: '📱 휴대폰',
  return: '↩️ 복귀',
  focus: '🎯 집중',
  normal: '✅ 특이없음',
  other: '📝 메모',
};

export const AttendanceTableView: React.FC<AttendanceTableViewProps> = ({
  seats,
  onStudentClick,
  roomId,
  targetDate,
}) => {
  // 기본 정렬 = 문제 학생 위로 (미출석·지각·미복귀 먼저)
  const [sortField, setSortField] = useState<SortField>('problem');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // ⭐ 교시 탭 — null=현재(실시간), 숫자=그 교시 시점 스냅샷
  const [selectedPeriod, setSelectedPeriod] = useState<number | null>(null);
  const [periodMetas, setPeriodMetas] = useState<PeriodMeta[]>([]);
  const [gridStudents, setGridStudents] = useState<GridStudent[]>([]);
  const [gridLoading, setGridLoading] = useState(false);
  const [gridError, setGridError] = useState<string | null>(null);

  // 교시 그리드 fetch — roomId가 있고 사용자가 교시 탭 선택 가능 상태에서 1회 호출
  useEffect(() => {
    if (!roomId) return;
    const fetchGrid = async () => {
      setGridLoading(true);
      setGridError(null);
      try {
        const params: Record<string, string> = { room_id: roomId };
        if (targetDate) params.target_date = targetDate;
        // apiClient baseURL=/api 이므로 '/api' 빼야 함 — 다른 페이지 패턴(/v1/...) 따름
        const res = await apiClient.get('/v1/owner/attendance-period-grid', { params });
        const data = res.data || {};
        setPeriodMetas(data.periods || []);
        setGridStudents(data.students || []);
      } catch (e: any) {
        setGridError(e?.response?.data?.detail || e?.message || '교시별 데이터 조회 실패');
        setPeriodMetas([]);
        setGridStudents([]);
      } finally {
        setGridLoading(false);
      }
    };
    fetchGrid();
  }, [roomId, targetDate]);

  // 학생 id → 그 교시의 grid status 빠른 조회
  const gridStatusByStudent = useMemo(() => {
    const map: Record<string, Record<string | number, string>> = {};
    gridStudents.forEach(s => { map[s.student_id] = s.statuses || {}; });
    return map;
  }, [gridStudents]);

  const getAttendanceStatus = (seat: RuntimeSeat): string => {
    // ⭐ 교시 탭 선택 시: 그 교시 시점의 grid 데이터 사용
    if (selectedPeriod !== null) {
      const studentId = (seat as any).student_id;
      if (!studentId) return 'not_scheduled';
      const statuses = gridStatusByStudent[studentId];
      if (!statuses) return 'not_scheduled';
      const gridStatus = statuses[selectedPeriod] || statuses[String(selectedPeriod)];
      return GRID_TO_STATUS[gridStatus] || 'not_scheduled';
    }

    // 기본(실시간): 기존 schedule_status 사용
    if (seat.schedule_status === 'present') {
      return seat.is_late ? 'present_late' : 'present';
    }
    if (seat.schedule_status === 'arriving') return 'arriving';
    if (seat.schedule_status === 'exited') return 'exited';
    if (seat.schedule_status === 'out') {
      // 미복귀 체크: return_overdue_minutes가 있으면 지연 상태
      if (seat.return_overdue_minutes !== undefined && seat.return_overdue_minutes > 0) {
        // 5분 초과면 심각(빨강), 5분 이하면 경고(주황)
        return seat.return_overdue_minutes > 5 ? 'out_overdue' : 'out_warning';
      }
      return 'out';
    }
    if (seat.schedule_status === 'absent') return 'absent';
    if (seat.schedule_status === 'exempt') return 'exempt';
    return 'not_scheduled';
  };

  const filteredAndSortedSeats = useMemo(() => {
    let result = [...seats];

    // ⭐ 교시 탭 선택 시: 배정된 학생은 모두 표시 (빈 좌석만 제외)
    //    그 교시 스케줄이 아닌 학생은 숨기지 않고 '스케줄없음'으로 표시 →
    //    스케줄 등록한 학생이 특정 교시 탭에서 사라지는 혼란 방지. 문제 우선 정렬로 미출석은 위, 스케줄없음은 아래.
    if (selectedPeriod !== null) {
      result = result.filter(seat => !!(seat as any).student_id);  // 빈 좌석만 제외
    }

    // 검색 필터
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(seat =>
        seat.student_name?.toLowerCase().includes(query) ||
        seat.seat_code.toLowerCase().includes(query)
      );
    }

    // 상태 필터
    if (statusFilter !== 'all') {
      result = result.filter(seat => {
        const status = getAttendanceStatus(seat);
        if (statusFilter === 'late') return status === 'present_late';
        if (statusFilter === 'present') return status === 'present';
        // 외출은 미복귀(경고·심각)도 같은 '외출'이다 — 걸러지면 정작 찾아야 할 학생이 빠진다
        if (statusFilter === 'out') return status.startsWith('out');
        return status === statusFilter;
      });
    }

    // 정렬
    result.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'problem': {
          // 문제 학생 위로: 상태 우선순위 → 같으면 좌석번호 순
          const ra = PROBLEM_RANK[getAttendanceStatus(a)] ?? 99;
          const rb = PROBLEM_RANK[getAttendanceStatus(b)] ?? 99;
          comparison = ra - rb;
          if (comparison === 0) {
            comparison = (parseInt(a.seat_code, 10) || 0) - (parseInt(b.seat_code, 10) || 0);
          }
          break;
        }
        case 'seat_code':
          // 숫자로 정렬 (1, 2, 3... 순서)
          const aNum = parseInt(a.seat_code, 10) || 0;
          const bNum = parseInt(b.seat_code, 10) || 0;
          comparison = aNum - bNum;
          break;
        case 'student_name':
          comparison = (a.student_name || '').localeCompare(b.student_name || '');
          break;
        case 'status':
          comparison = getAttendanceStatus(a).localeCompare(getAttendanceStatus(b));
          break;
        case 'check_in_time':
          const aSchedule = a.expected_check_in || '';
          const bSchedule = b.expected_check_in || '';
          comparison = aSchedule.localeCompare(bSchedule);
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [seats, searchQuery, statusFilter, sortField, sortDirection, selectedPeriod, gridStatusByStudent]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <span className="text-gray-400 ml-1">-</span>;
    return (
      <span className="ml-1">
        {sortDirection === 'asc' ? '\u2191' : '\u2193'}
      </span>
    );
  };

  const formatTime = (timeString?: string) => {
    if (!timeString) return '-';
    return new Date(timeString).toLocaleTimeString('ko-KR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="bg-white rounded-lg shadow">
      {/* ⭐ 교시 탭 — 현재(실시간) / 1~N교시 */}
      <div className="border-b border-gray-200 bg-gray-50 px-2 py-2 flex items-center gap-1 flex-wrap">
        <button
          onClick={() => setSelectedPeriod(null)}
          className={`px-2.5 sm:px-3 py-1 text-xs rounded-full border transition-colors min-w-[2rem] ${
            selectedPeriod === null
              ? 'bg-blue-600 text-white border-blue-600 font-semibold'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-blue-50'
          }`}
        >
          현재
        </button>
        {periodMetas.length === 0 && !gridLoading && (
          <span className="text-xs text-gray-400 ml-2">
            {roomId ? '교시 시간표 없음' : '룸 선택 필요'}
          </span>
        )}
        {gridLoading && (
          <span className="text-xs text-gray-400 ml-2">불러오는 중...</span>
        )}
        {gridError && (
          <span className="text-xs text-red-500 ml-2">⚠️ {gridError}</span>
        )}
        {periodMetas.map((p, i) => {
          // 응답 형식: period_number(0부터) + period_name("0교시")
          const idx = p.period_number ?? p.period_index ?? p.index ?? i;
          const label = p.period_name || p.label || p.name || `${idx}교시`;
          const tooltip = p.start_time && p.end_time
            ? `${label} (${p.start_time}~${p.end_time})`
            : label;
          /* 🔴 휴대폰에서는 숫자만 보여준다 (2026-09-05 대표 요청).
                '0교시 08:00' 처럼 다 적으면 버튼이 커져 세 줄을 잡아먹는다.
                '3교시' → '3', 그 외 이름(자습실개방 등)은 앞 두 글자만.
                PC 는 종전대로 이름과 시작 시각을 다 보여준다. */
          const numMatch = /^(\d+)\s*교시$/.exec(label.trim());
          const shortLabel = numMatch ? numMatch[1] : label.trim().slice(0, 2);
          return (
            <button
              key={idx}
              onClick={() => setSelectedPeriod(idx)}
              title={tooltip}
              className={`px-2.5 sm:px-3 py-1 text-xs rounded-full border transition-colors min-w-[2rem] ${
                selectedPeriod === idx
                  ? 'bg-blue-600 text-white border-blue-600 font-semibold'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-blue-50'
              }`}
            >
              {/* 휴대폰: 숫자만 / PC: 이름 + 시작 시각 */}
              <span className="sm:hidden">{shortLabel}</span>
              <span className="hidden sm:inline">
                {label}
                {p.start_time && (
                  <span className="ml-1 text-[10px] opacity-70">
                    {p.start_time.slice(0, 5)}
                  </span>
                )}
              </span>
            </button>
          );
        })}
        {selectedPeriod !== null && (
          <span className="text-[10px] text-blue-600 ml-2 font-medium">
            ⓘ 이 교시 시점의 출석 상황을 보고 있습니다
          </span>
        )}
      </div>

      {/* 필터 영역 */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center gap-4">
          {/* 검색 */}
          <div className="flex-1 max-w-xs">
            <input
              type="text"
              placeholder="학생명 또는 좌석번호 검색..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>

          {/* 상태 필터 */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">상태:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="all">전체</option>
              <option value="present">출석</option>
              <option value="late">지각</option>
              <option value="out">외출</option>
              <option value="absent">미출석</option>
              <option value="exempt">출석면제</option>
              <option value="not_scheduled">스케줄없음</option>
            </select>
          </div>

          {/* 결과 수 */}
          <div className="text-sm text-gray-500">
            총 {filteredAndSortedSeats.length}명
          </div>
        </div>
      </div>

      {/* 테이블 */}
      {/* 🔴 w-full 만 두면 좁은 화면에서 표가 눌려 머리글자가 세로로 쌓인다.
             min-w-max 로 제 폭을 지키게 하고, 휴대폰에서는 옆으로 밀어서 본다.
             (휴대폰은 이 목록이 기본 화면이라 특히 중요하다 — 2026-09-05) */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-max">
          <thead className="bg-gray-50">
            <tr>
              <th
                className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100"
                style={{ width: '56px' }}
                onClick={() => handleSort('seat_code')}
                title="좌석번호"
              >
                좌석
                <SortIcon field="seat_code" />
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('student_name')}
              >
                학생명
                <SortIcon field="student_name" />
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('problem')}
                title="문제 학생(미출석·지각·미복귀)을 위로 정렬"
              >
                출석상태
                <SortIcon field="problem" />
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('check_in_time')}
              >
                스케줄
                <SortIcon field="check_in_time" />
              </th>
              {/* ⭐ 휴대폰 검사 (Step 2 — 데이터 모델 확인 후 연결, 현재는 "-") */}
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                휴대폰
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                비고
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredAndSortedSeats.map((seat) => {
              const status = getAttendanceStatus(seat);
              const badge = STATUS_BADGES[status] || STATUS_BADGES.not_scheduled;

              return (
                <tr
                  key={seat.seat_id}
                  className={`hover:bg-gray-50 ${onStudentClick ? 'cursor-pointer' : ''}`}
                  onClick={() => {
                    if (onStudentClick) {
                      onStudentClick(seat.seat_id);
                    }
                  }}
                >
                  <td className="px-2 py-3 whitespace-nowrap" style={{ width: '56px' }}>
                    <span className="text-sm font-medium text-gray-900">
                      {seat.seat_code}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="text-sm text-gray-900">
                      {seat.student_name || '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${badge.className}`}>
                      {badge.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {seat.expected_check_in && seat.expected_check_out
                      ? `${seat.expected_check_in}~${seat.expected_check_out}`
                      : '-'}
                  </td>
                  {/* 휴대폰 — 원터치 전화(tel:). 폰/연결된 PC에서 통화앱 바로 열림 */}
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                      {seat.student_phone ? (
                        <a
                          href={`tel:${telHref(seat.student_phone)}`}
                          title={`학생에게 전화 (${seat.student_phone})`}
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-xs font-medium hover:bg-blue-100"
                        >
                          📞 학생
                        </a>
                      ) : null}
                      {seat.student_parent_phone ? (
                        <a
                          href={`tel:${telHref(seat.student_parent_phone)}`}
                          title={`학부모에게 전화 (${seat.student_parent_phone})`}
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-xs font-medium hover:bg-emerald-100"
                        >
                          📞 학부모
                        </a>
                      ) : null}
                      {!seat.student_phone && !seat.student_parent_phone ? (
                        <span className="text-gray-300">-</span>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    <div className="flex flex-col gap-1">
                      {/* 외출/복귀/경과 (기존) */}
                      <div className="flex flex-wrap items-center gap-x-2">
                        {seat.outing_type && (
                          <span className="text-blue-600">
                            {OUTING_LABELS[seat.outing_type] || '외출'}
                          </span>
                        )}
                        {seat.schedule_status === 'out' && seat.expected_return_time && (
                          <span className={seat.return_overdue_minutes && seat.return_overdue_minutes > 0
                            ? (seat.return_overdue_minutes > 5 ? 'text-red-600 font-medium' : 'text-orange-500')
                            : 'text-gray-500'}>
                            {seat.return_overdue_minutes && seat.return_overdue_minutes > 0
                              ? `${seat.return_overdue_minutes}분 지연`
                              : `${seat.expected_return_time}까지`}
                          </span>
                        )}
                        {seat.schedule_status === 'absent' && seat.absence_minutes && (
                          <span className="text-red-600">
                            {seat.absence_minutes}분 경과
                          </span>
                        )}
                      </div>
                      {/* 사유서 — 클릭 없이 표시 */}
                      {seat.exempt_reason && (
                        <span className="inline-block w-fit px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 text-xs">
                          📄 사유서: {seat.exempt_reason}
                        </span>
                      )}
                      {/* 🔴 승인 대기 사유서 — 승인된 것(보라)과 **색을 달리한다**.
                          같은 색이면 원장이 "처리됐구나" 로 읽어 승인을 안 누른다.
                          2026-09-19: 학생이 사유서를 내도 승인 전엔 표시가 없어서,
                          원장이 모르고 미출석 알림을 보내던 문제. */}
                      {seat.pending_exception && (
                        <span className="inline-block w-fit px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300 text-xs font-medium">
                          ⏳ {seat.pending_exception.type} 사유서 제출됨 · 승인 대기
                          {seat.pending_exception.time ? ` (${seat.pending_exception.time})` : ''}
                        </span>
                      )}
                      {/* 순찰 메모 — 클릭 없이 표시 */}
                      {seat.last_note?.note_type && (
                        <span
                          className="inline-block w-fit px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-xs"
                          title={seat.last_note.note_text || ''}
                        >
                          {NOTE_LABELS[seat.last_note.note_type] || `📝 ${seat.last_note.note_type}`}
                          {seat.last_note.note_text ? `: ${seat.last_note.note_text}` : ''}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}

            {filteredAndSortedSeats.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                  {searchQuery || statusFilter !== 'all'
                    ? '검색 조건에 맞는 학생이 없습니다.'
                    : '출석 데이터가 없습니다.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
